// composables/useIntegration.ts
import { apiGet, apiGetHeaders } from '@/util/api'
import { IntegrationFactory } from '../factories/IntegrationFactory'
import { useAuthStore } from '@/stores/auth'
import type { Client } from '@/interfaces/client'
import { useClientStore } from '@/stores/client'

export function useIntegration() {
    const authStore = useAuthStore()
    const clientStore = useClientStore()

    const login = async (integrationName: string, credentials: any) => {
        const strategy = IntegrationFactory.getStrategy(integrationName)
        try {
            const response = await strategy.login(credentials)
            const data = response.data
            const token = response.headers?.Authorization || response.headers?.authorization

            // Case 1: Skip OTP (fully authenticated)
            if (data?.should_skip_otp && token) {
                return { data, headers: response.headers }
            }
            // Case 2: OTP stage (no token, but otp_token exists)
            if (data?.otp_token) {
                return { data } // Let frontend handle showing OTP modal
            }

            // Case 3: Regular login with token (e.g., SMS verification completed)
            if (token) {
                try {
                    if (!data.data?.default_client_id) {
                        const res = await apiGetHeaders<{ clients: Client[] }>(
                            `/clients?page=1&per_page=1`,
                            token
                        )
                        clientStore.setCurrentClient(res.clients[0])
                    } else {
                        const res = await apiGetHeaders<Client>(
                            `/clients/${data.data.default_client_id}`,
                            token
                        )
                        clientStore.setCurrentClient(res)
                        clientStore.setDefaultClient(res)
                    }
                } catch (error) {
                    console.error('Error setting default client:', error)
                }
                authStore.setLoginState(token, data.data)
                return { data, headers: response.headers }
            }

            // Fallback if none of the cases apply
            throw new Error('Authentication failed: No token or OTP token received')
        } catch (error) {
            console.error('Authentication failed:', error)
            throw error
        }
    }

    const logout = () => {
        authStore.logout()
    }

    const getLoginFormComponent = (integrationName: string) => {
        return IntegrationFactory.getLoginFormComponent(integrationName)
    }

    return {
        login,
        logout,
        getLoginFormComponent
    }
}
